import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { StardustInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * STARDUST — third cartridge.
 *
 * Vertical meteor shooter with collectible-ammo twist. Astrosmash + Asteroids
 * heritage; the original mechanic is the white shards: every cracked meteor
 * drops loose ammo you can collect, so trigger-mashing starves you of bullets.
 *
 * MVP (sc-2855): three-tier splitting meteors, horizontal saucer with bomb
 * drop, finite ammo with shard refills, distance/time-based wave ramp.
 */
const module_: GameModule = {
  id: 'stardust',
  title: 'STARDUST',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'STEER' },
    { keys: 'UP / DOWN', action: 'THRUST' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Pew: bullet fire. Quick descending blip — short and quiet enough
    // that sustained fire doesn't fatigue the ear.
    ctx.registerSfx('shoot', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 1100 }, { t: 35, f: 660 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.12 }, { t: 35, g: 0.0 }],
    });
    // Meteor crack: short noise pop, much quieter than crash so meteors
    // don't dominate when many are dying at once.
    ctx.registerSfx('crack', {
      wave: 'noise',
      pitch: [{ t: 0, f: 240 }, { t: 110, f: 90 }],
      gain: [{ t: 0, g: 0 }, { t: 5, g: 0.22 }, { t: 110, g: 0.0 }],
    });
    // Shard pickup: bright rising chime, kept short so back-to-back
    // pickups don't blur into a wall of treble.
    ctx.registerSfx('pickup', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 660 }, { t: 70, f: 990 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.22 }, { t: 70, g: 0.0 }],
    });
    // Ammo empty: low thunk so the player knows why nothing happened.
    ctx.registerSfx('empty', {
      wave: 'sine',
      pitch: [{ t: 0, f: 130 }, { t: 60, f: 80 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.18 }, { t: 60, g: 0.0 }],
    });
    // Saucer entry: short warning warble. Plays once per saucer spawn so
    // the player knows to look up.
    ctx.registerSfx('saucer', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 300 }, { t: 120, f: 220 }, { t: 240, f: 300 }],
      gain: [{ t: 0, g: 0 }, { t: 12, g: 0.2 }, { t: 240, g: 0.0 }],
      fm: { ratio: 0.5, index: 30 },
    });
    // Crash: short noise burst with a sub-bass thump.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 80 }, { t: 200, f: 40 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 200, g: 0.0 }],
    });
    // Wave up: ascending fanfare.
    ctx.registerSfx('wave-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new StardustInstance(ctx);
  },
};

export default module_;
